import React, { useState, useEffect } from "react";
import {
  SFGridContainer,
  SFGridItem,
  SFContentContainer,
  SFTabs,
  SFTabPanel,
  SFUseStyled,
  SFStack,
  SFToggleButtons,
  SFLoadingPage,
} from "@sf-core";
import CourseList from "../../components/list";
import BasicDetails from "../../components/basicDetails";
import MenuVerticalTabs from "../../components/domainPage/menuVerticalTabs";
import SkillPathList from "../list/skillPathList";
import { useTranslation } from "react-i18next";

const { styled } = SFUseStyled();

const CourseListContainer = styled(SFStack)(({ theme }) => ({
  height: "100%",
}));

const SFTabPanelStyled = styled(SFTabPanel)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: 0,
  flex: "1 0 auto",
  "&[role=tabpanel][hidden]": {
    flex: 0,
  },
}));

const StyledSFGridItem = styled(SFGridItem)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("lg")]: {
    minHeight: "700px",
  },
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  marginTop: theme.spacing(3) + "!important",
  marginBottom: theme.spacing(2) + " !important",
  paddingRight: theme.spacing(1.5),
  height: 0,
  flex: "1 0 auto",
  overflowX: "hidden",
  ...theme.scrollable,
}));

const DomainPage = ({
  backgroundImage = null,
  domain = {},
  menuTabs = [],
  allSkills = [],
  currentState = [],
  contentOptions = [],
  setContentOptions,
  setCurrentState,
  skillPathLevels = [],
  recommendedSkillPathLevels = [],
  courses = [],
  recommendedSkillPaths = [],
  relatedDomains = [],
  showAvatar,
  subTitle,
  getDropDownActions,
  isSkillGoalPage = false,
}) => {
  const { t } = useTranslation();
  const [menuTab, setMenuTab] = useState(0);
  const [currentContentOption, setCurrentContentOption] = useState(null);

  useEffect(() => {
    setCurrentContentOption(contentOptions[0]);
  }, [contentOptions]);

  useEffect(() => {
    if (recommendedSkillPaths.length > 0) {
      setCurrentState((prv) => ({
        ...prv,
        recommendedSkillPathLevelId: recommendedSkillPathLevels[0]?.id,
      }));
      setContentOptions((prevContentOptions) =>
        prevContentOptions.some((item) => item.id === 2)
          ? prevContentOptions
          : [
            ...prevContentOptions,
            { id: 2, label: t("jobScreen.tabSkillPaths") },
          ]
      );
    }
  }, [recommendedSkillPaths.length]);

  const onChangeHandler = (selected) => {
    setCurrentState((prv) => ({
      ...prv,
      selectedSkills: selected,
    }));
  };

  return (
    <SFContentContainer
      backgroundImage={backgroundImage}
      showTopMargin
      showSideMargins
    >
      <SFGridContainer sx={{ flex: 1 }} alignItems="stretch" spacing={8}>
        <StyledSFGridItem xs={12} lg={6}>
          <SFStack spacing={6} height="100%">
            <BasicDetails
              item={domain}
              showAvatar={showAvatar}
              subTitle={subTitle}
              getDropDownActions={getDropDownActions}
            />
            <SFTabs
              items={menuTabs}
              value={menuTab}
              handleChange={(index) => setMenuTab(index)}
            />
            <StyledStack>
              <SFTabPanel index={0} value={menuTab}>
                {allSkills.length !== 0 ? (
                  <SFToggleButtons
                    disabled={isSkillGoalPage}
                    noHover={isSkillGoalPage}
                    haveCollapsibleItems={true}
                    limit={20}
                    items={allSkills.map((x) => ({
                      label: x.name,
                      value: x.id,
                    }))}
                    onChange={onChangeHandler}
                  />
                ) : (
                  <SFLoadingPage />
                )}
              </SFTabPanel>
              <SFTabPanel index={1} value={menuTab}>
                {relatedDomains?.map((item) => (
                  <MenuVerticalTabs key={item.jobRoleId} item={item} />
                ))}
              </SFTabPanel>
            </StyledStack>
          </SFStack>
        </StyledSFGridItem>
        <StyledSFGridItem xs={12} lg={6}>
          <CourseListContainer>
            {currentState.skillPathLevelId ||
            currentState.recommendedSkillPathLevelId ? (
                <>
                  <SFTabs
                    borderBottom={0}
                    items={contentOptions}
                    value={currentContentOption?.id}
                    handleChange={(id) =>
                      setCurrentContentOption(() => contentOptions[id - 1])
                    }
                  />
                  {currentContentOption?.id === 1 ? (
                    <>
                      <SFTabs
                        items={skillPathLevels}
                        value={currentState.skillPathLevelId}
                        handleChange={(skillPathLevelId) =>
                          setCurrentState((prv) => ({ ...prv, skillPathLevelId }))
                        }
                      />
                      {skillPathLevels.map((item, index) => (
                        <SFTabPanelStyled
                          key={index}
                          index={item.id}
                          value={currentState.skillPathLevelId}
                        >
                          <CourseList
                            domain={domain}
                            items={courses}
                            skillPathLevelId={currentState.skillPathLevelId}
                            courseDescriptionLines={4}
                          />
                        </SFTabPanelStyled>
                      ))}
                    </>
                  ) : (
                    <>
                      <SFTabs
                        items={recommendedSkillPathLevels}
                        value={currentState.recommendedSkillPathLevelId}
                        handleChange={(recommendedSkillPathLevelId) =>
                          setCurrentState((prv) => ({
                            ...prv,
                            recommendedSkillPathLevelId,
                          }))
                        }
                      />
                      {recommendedSkillPathLevels.map((item, index) => (
                        <SFTabPanelStyled
                          key={index}
                          index={item.id}
                          value={currentState.recommendedSkillPathLevelId}
                        >
                          <SkillPathList
                            domain={domain}
                            skillPaths={recommendedSkillPaths}
                            skillPathLevelId={
                              currentState.recommendedSkillPathLevelId
                            }
                            skillPathDescriptionLines={4}
                          />
                        </SFTabPanelStyled>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <SFLoadingPage />
              )}
          </CourseListContainer>
        </StyledSFGridItem>
      </SFGridContainer>
    </SFContentContainer>
  );
};

export default DomainPage;
